import type { LscMember } from '../types';
import { COMMISSIONER_REMOVED_DEACTIVATION_REASON, MERGED_DEACTIVATION_REASON } from './memberFilter';

export function formatMemberStatus(status?: string | null): string {
  if (status === 'imported_unclaimed') return 'Pending Link';
  if (status === 'inactive') return 'Inactive';
  if (!status) return 'Active';
  return status.replace(/_/g, ' ');
}

export function getMemberDiscordLabel(member: LscMember): string {
  const neonsportz = (member.source_metadata?.neonsportz as Record<string, unknown>) || {};
  const username = neonsportz.discord_username as string | undefined;
  const discordId = neonsportz.discord_id as string | undefined;
  if (username) return username;
  if (discordId) return `Discord ID ${discordId}`;
  const identity = (member.identities || []).find((item) => item.identity_type === 'discord_username');
  if (identity?.identity_value) return identity.identity_value;
  return '—';
}

export function getMemberInGameLabel(member: LscMember): string {
  const neonsportz = (member.source_metadata?.neonsportz as Record<string, unknown>) || {};
  const inGame = neonsportz.in_game_name as string | undefined;
  if (inGame) return inGame;
  const gameIdentity = member.source_metadata?.game_identity as Record<string, unknown> | undefined;
  if (gameIdentity?.value) return String(gameIdentity.value);
  const identity = (member.identities || []).find((item) => item.identity_type === 'in_game_name');
  if (identity?.identity_value) return identity.identity_value;
  return '—';
}

export function formatMemberTwitchLogin(member: LscMember): string {
  if (member.twitch_login) return `@${member.twitch_login}`;
  if (member.twitch_user_id) return `id:${member.twitch_user_id}`;
  return 'Missing';
}

export function streamPlatformFieldsFromMember(member: LscMember): {
  stream_platform: 'twitch' | 'youtube';
  youtube_channel: string;
} {
  const accounts = member.stream_accounts || [];
  const youtubeAccount = accounts.find(
    (account) => String(account?.platform || '').toLowerCase() === 'youtube'
  );
  const handle = String(youtubeAccount?.handle || '').trim();
  const channel = handle
    ? handle.startsWith('@')
      ? handle
      : `@${handle}`
    : String(youtubeAccount?.account_id || '').trim();
  return {
    stream_platform: memberPrimaryIsYouTube(member) ? 'youtube' : 'twitch',
    youtube_channel: memberPrimaryIsYouTube(member) ? channel : '',
  };
}

export function memberPrimaryIsYouTube(member: LscMember): boolean {
  const explicit = String(member.stream_platform || '').toLowerCase();
  if (explicit === 'youtube' || explicit === 'twitch') return explicit === 'youtube';
  const accounts = member.stream_accounts || [];
  const primary = accounts.find((account) => account?.primary) || accounts[0];
  if (primary && String(primary.platform || '').toLowerCase() === 'youtube') return true;
  return String(member.stream_provider || '').toLowerCase() === 'youtube';
}

export function memberWasRemovedByCommissioner(member: LscMember): boolean {
  return member.deactivation_reason === COMMISSIONER_REMOVED_DEACTIVATION_REASON;
}

function memberIsUnclaimedSeat(member: LscMember): boolean {
  if (member.user_uid) return false;
  if (member.role && member.role !== 'member') return false;
  if (member.status === 'inactive' || memberWasRemovedByCommissioner(member)) return false;
  return (
    member.status === 'imported_unclaimed' ||
    member.status === 'invited' ||
    member.status === 'active' ||
    !member.status
  );
}

export function memberClaimSignInMethod(member: LscMember): 'google' | 'twitch' {
  return String(member.stream_platform || '').trim().toLowerCase() === 'youtube' ? 'google' : 'twitch';
}

export function memberCanReceiveClaimLink(member: LscMember): boolean {
  return memberIsUnclaimedSeat(member);
}

export function memberCanCopyClaimLink(member: LscMember): boolean {
  return memberIsUnclaimedSeat(member);
}

export function formatMemberDeactivationReason(reason?: string | null): string | null {
  if (!reason) return null;
  if (reason === MERGED_DEACTIVATION_REASON) return 'Merged into imported member';
  if (reason === COMMISSIONER_REMOVED_DEACTIVATION_REASON) return 'Removed by commissioner';
  return reason.replace(/_/g, ' ');
}
